import { useQuery, gql } from '@apollo/client'; 
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { UserProfileFragment } from '../fragments/UserProfile';
import { PageSEOFragment } from '../fragments/pages/PageSEOFragment';
import { OptimizedImageFragment } from '../fragments/artikler/ArtikkelSEOFragment';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import Main from '../components/Main/Main';
import Container from '../components/Container/Container';
import NavigationMenu from '../components/NavigationMenu/NavigationMenu';
import SEO from '../components/SEO/SEO';
import SchemaMarkup from '../components/SchemaMarkup/SchemaMarkup';
import OmossHero from '../components/AboutPage/OmossHero/OmossHero';
import OmossValues from '../components/AboutPage/OmossValues/OmossValues';
import OmossInfinity from '../components/AboutPage/OmossInfinity/OmossInfinity';
import OmossTextContent from '../components/AboutPage/OmossTextContent/OmossTextContent';
import EmployeeGrid from '../components/ContactPage/EmployeeGrid/EmployeeGrid';
import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import TopHero from '../components/skeletonloader/TopHero';
import Head from 'next/head';
import { createSchema, createWebPageSchema, createBreadcrumbSchema } from '../lib/schema';
import { getDispatchedPageSeo } from '../lib/page-seo-helpers';
import { POLYLANG_URI_FIELDS } from '../lib/graphql-polylang-fields';
import { buildSiteUrl } from '../lib/polylang';
import { fetchHeaderFooterSharedData } from '../lib/fetch-header-footer-shared-data';
import { ANSATT_LANDING_PRESENCE_FIELDS } from '../fragments/ansatt/AnsattContentFragment';

export default function OmOss(props) {
  const router = useRouter();
  const { data, loading = false, error = null } = { data: props.data || {} };

  if (error) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const viewer = data?.viewer;
  const page = data?.page || data?.nodeByUri;
  const pageSeo = getDispatchedPageSeo(router.asPath, page, 'om-oss');

  // Find the sections from the flexible content
  const heroSection = page?.omoss?.omOssSide?.find(
    section => section.fieldGroupName === 'OmossOmOssSideHeroLayout'
  );

  const infinitySection = page?.omoss?.omOssSide?.find(
    section => section.fieldGroupName === 'OmossOmOssSideInfinitySectionLayout'
  );

  const valuesSection = page?.omoss?.omOssSide?.find(
    section => section.fieldGroupName === 'OmossOmOssSideValuesSectionLayout'
  );

  const textContentSection = page?.omoss?.omOssSide?.find(
    section => section.fieldGroupName === 'OmossOmOssSideTextContentSectionLayout'
  );

  // Data comes from getStaticProps, so loading is false on client; show content immediately
  const isLoading = loading;

  // Get the page description from the hero section or fall back to site description
  const pageDescription = heroSection?.subText || siteDescription;
  const defaultAboutDescription =
    pageSeo.code === 'en'
      ? 'Mediabooster is a fast-growing media agency in Oslo, delivering digital marketing, web design, and technology services in Norway and internationally.'
      : 'Mediabooster Norge AS er et raskt voksende Mediebyrå i Oslo, som leverer tjenester både i norge og globalt, til privat og offentlig.';
  const seoDescription = page?.seo?.description || pageDescription || defaultAboutDescription;
  
  // Helper to get optimized social image (1200x630 for OG tags)
  const getOptimizedSocialImage = (imageNode) => {
    if (!imageNode) return 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
    
    const sizes = imageNode.mediaDetails?.sizes || [];
    // Look for large size for social media (1024px+)
    const large = sizes.find(size => size.name === 'large');
    const mediumLarge = sizes.find(size => size.name === 'medium_large');
    
    return large?.sourceUrl || mediumLarge?.sourceUrl || imageNode.mediaItemUrl || imageNode.sourceUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
  };
  
  // Get featured image from hero section or text content section
  const featuredImage = heroSection?.heroImage?.node || textContentSection?.image?.node;
  const socialImage = getOptimizedSocialImage(featuredImage);
  const socialImageAlt = featuredImage?.altText || 'Mediabooster - Digital markedsføring og webdesign';

  // Get hero image for preloading - use large size for faster initial load
  const getOptimizedImageSrcForPreload = (imageNode) => {
    if (!imageNode) return null;
    const sizes = imageNode.mediaDetails?.sizes || [];
    // Use large (1024px) for preload - good balance of quality and file size
    const large = sizes.find(size => size.name === 'large');
    const mediumLarge = sizes.find(size => size.name === 'medium_large');
    return large?.sourceUrl || mediumLarge?.sourceUrl || imageNode.mediaItemUrl || imageNode.sourceUrl;
  };
  
  const heroImageSrc = heroSection?.heroImage?.node ? getOptimizedImageSrcForPreload(heroSection.heroImage.node) : null;

  // Generate schema for about page
  const pageSchema = useMemo(() => {
    const aboutTitle =
      pageSeo.code === 'en'
        ? `About us - ${siteTitle || 'Mediabooster'}`
        : `Om oss - ${siteTitle || 'Mediabooster'}`;
    const canonicalUrl = pageSeo.canonicalUrl;

    return createSchema([
      createWebPageSchema({
        title: aboutTitle,
        description: seoDescription,
        url: pageSeo.canonicalPath,
        imageUrl: socialImage,
      }),
      {
        '@type': 'AboutPage',
        '@id': `${canonicalUrl}#aboutpage`,
        name: aboutTitle,
        description: seoDescription,
        url: canonicalUrl,
        mainEntity: {
          '@id': `${buildSiteUrl('/')}#organization`,
        },
      },
      createBreadcrumbSchema(pageSeo.breadcrumbs),
    ]);
  }, [siteTitle, seoDescription, socialImage, pageSeo]);

  return (
    <>
      <SchemaMarkup schema={pageSchema} />
      <Head>
        {/* Preload critical hero image */}
        {heroImageSrc && (
          <link 
            rel="preload" 
            href={heroImageSrc} 
            as="image" 
            fetchpriority="high"
          />
        )}
        {/* Preconnect to WordPress domain for faster image loading */}
        <link rel="preconnect" href="https://mediaboosterhe.wpenginepowered.com" />
        <link rel="dns-prefetch" href="https://mediaboosterhe.wpenginepowered.com" />
      </Head>
      <SEO
        locale={pageSeo.locale}
        hreflangAlternates={pageSeo.hreflangAlternates}
        seo={{
          title:
            pageSeo.code === 'en'
              ? `About us - ${siteTitle || 'Mediabooster'}`
              : `Om oss - ${siteTitle || 'Mediabooster'}`,
          description: seoDescription,
          canonicalUrl: page?.seo?.canonicalUrl || pageSeo.canonicalUrl,
          robots: page?.seo?.robots,
          openGraph: {
            title:
              pageSeo.code === 'en'
                ? `About us - ${siteTitle || 'Mediabooster'}`
                : `Om oss - ${siteTitle || 'Mediabooster'}`,
            description: seoDescription,
            url: page?.seo?.canonicalUrl || pageSeo.canonicalUrl,
            siteName: siteTitle || 'Mediabooster',
            type: 'website',
            image: {
              url: socialImage,
              secureUrl: socialImage,
              width: 1200,
              height: 630,
              alt: socialImageAlt,
            },
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title:
              pageSeo.code === 'en'
                ? `About us - ${siteTitle || 'Mediabooster'}`
                : `Om oss - ${siteTitle || 'Mediabooster'}`,
            description: seoDescription,
            image: socialImage,
          },
        }}
      />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        viewer={viewer}
        sharedData={props.sharedData}
        languageCode={pageSeo.code}
      />
      <Main>
        {isLoading ? (
          <TopHero />
        ) : (
          <>
            {heroSection && <OmossHero data={heroSection} />}
            {infinitySection && <OmossInfinity data={infinitySection} />}
            {valuesSection && <OmossValues data={valuesSection} />}
                        <EmployeeGrid data={data?.employees} />
            {textContentSection && <OmossTextContent data={textContentSection} />}
          </>
        )}
      </Main>
      <Footer 
        title={siteTitle} 
        menuItems={primaryMenu}
        settings={data}
        sharedData={props.sharedData}
        languageCode={pageSeo.code}
      />
    </>
  );
}

OmOss.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${UserProfileFragment}
  ${PageSEOFragment}
  ${OptimizedImageFragment}
  query GetOmOssPage(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    ...UserProfileFragment
    page(id: "111", idType: DATABASE_ID) {
      ...PageSEOFragment
      ${POLYLANG_URI_FIELDS}
      omoss {
        omOssSide {
          ... on OmossOmOssSideHeroLayout {
            fieldGroupName
            mainHeaderText
            buttonText {
              buttonLink {
                url
              }
              buttonText
            }
            heroImage {
              node {
                ...OptimizedImageFragment
              }
            }
            smallTopHeader
            subText
          }
          ... on OmossOmOssSideInfinitySectionLayout {
            fieldGroupName
            header
            button {
              buttonLink {
                url
              }
              buttonText
            }
            subHeader
            infinityImagesDesktop {
              nodes {
                id
                altText
                mediaItemUrl
              }
            }
            infinityImagesMobile {
              nodes {
                id
                altText
                mediaItemUrl
              }
            }
          }
          ... on OmossOmOssSideValuesSectionLayout {
            fieldGroupName
            header
            catchphrases {
              text
            }
            subHeader
          }
          ... on OmossOmOssSideTextContentSectionLayout {
            fieldGroupName
            header
            linkToVideo
            image {
              node {
                ...OptimizedImageFragment
              }
            }
            textLeft
            button {
              buttonText
              buttonLink {
                url
              }
            }
          }
        }
      }
    }
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    employees: ansatte(first: 100, where: { language: NB }) {
      nodes {
        title
        slug
        id
        ansattId
        ansatte {
          employeePhoto {
            node {
              ...OptimizedImageFragment
            }
          }
          role
          rekkefolge
          email
          linkedin
        }
        ${ANSATT_LANDING_PRESENCE_FIELDS}
      }
    }
  }
`;

OmOss.variables = () => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
  };
};

// Add getStaticProps for server-side data fetching with shared data
export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: OmOss.query,
      variables: OmOss.variables(),
      fetchPolicy: 'no-cache'
    });
    
    const sharedData = await fetchHeaderFooterSharedData(apolloClient, 'no');

    return {
      props: {
        data: pageData,
        sharedData,
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching om-oss data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}